import {currentUser, updateDocument} from '@/services/FirestoreService';
import {UNDERWRITING_SCENARIOS_COLLECTION} from '@/lib/underwriting/core/scenario/types';
import {findValidParentPortfolioId} from './parentPortfolio';
import {isPortfolioScenario, queryUnderwritingScenarios} from './scenario';
import type {UserAction} from '@/types/commonTypes';

function buildEditedOn(): UserAction {
  return {
    uid: currentUser()!.uid,
    name: currentUser()!.displayName ?? '',
    timestamp: new Date(),
  };
}

/**
 * Resolves the parent portfolio ID. Passing `null` explicitly means "we already know there is no
 * parent portfolio" (standalone building) — the lookup is skipped. `undefined` triggers a fresh
 * lookup via `findValidParentPortfolioId`.
 */
async function resolvePortfolioId(buildingId: string, knownPortfolioId?: string | null): Promise<string | null> {
  if (knownPortfolioId !== undefined) return knownPortfolioId;
  return findValidParentPortfolioId(buildingId);
}

/**
 * Soft-deletes a building from all underwriting scenarios when the building is deleted or
 * unlinked via the info window.
 *
 * - Portfolio buildings: sets `buildings.<buildingId>.deleted` in every portfolio scenario.
 * - Standalone buildings: sets `deleted` on the building's own base scenario.
 *
 * `knownPortfolioId` must be resolved by the caller **before** the deletion write — building
 * deletion removes the building from the portfolio's `linkedDocuments`, so a post-deletion
 * lookup returns null. Pass `null` to explicitly indicate a standalone building.
 */
export async function reflectPortfolioBuildingDeletion(
  buildingId: string, knownPortfolioId?: string | null,
): Promise<void> {
  const portfolioId = await resolvePortfolioId(buildingId, knownPortfolioId);
  const editedOn = buildEditedOn();

  if (portfolioId) {
    const scenarios = await queryUnderwritingScenarios('portfolio', portfolioId);
    await Promise.all(
      scenarios.map((scenario) => {
        if (!isPortfolioScenario(scenario)) return Promise.resolve();
        return updateDocument(UNDERWRITING_SCENARIOS_COLLECTION, scenario.id, {
          [`buildings.${buildingId}.deleted`]: editedOn.timestamp,
          editedOn,
        });
      }),
    );
  } else {
    const scenarios = await queryUnderwritingScenarios('building', buildingId);
    await Promise.all(
      scenarios.map((scenario) =>
        updateDocument(UNDERWRITING_SCENARIOS_COLLECTION, scenario.id, {
          deleted: editedOn.timestamp,
          editedOn,
        }),
      ),
    );
  }
}

/**
 * Clears the soft-delete on a building in all underwriting scenarios, restoring it to the
 * dashboard. Called when the user undoes a building deletion or unlink.
 *
 * - Portfolio buildings: clears `buildings.<buildingId>.deleted` in every portfolio scenario.
 * - Standalone buildings: sets `deleted: null` on the building's own base scenario (including
 *   soft-deleted scenarios, which need to be restored).
 */
export async function reflectPortfolioBuildingUndoDeletion(
  buildingId: string, knownPortfolioId?: string | null,
): Promise<void> {
  const portfolioId = await resolvePortfolioId(buildingId, knownPortfolioId);
  const editedOn = buildEditedOn();

  if (portfolioId) {
    const scenarios = await queryUnderwritingScenarios('portfolio', portfolioId);
    await Promise.all(
      scenarios.map((scenario) => {
        if (!isPortfolioScenario(scenario)) return Promise.resolve();
        return updateDocument(UNDERWRITING_SCENARIOS_COLLECTION, scenario.id, {
          [`buildings.${buildingId}.deleted`]: null,
          editedOn,
        });
      }),
    );
  } else {
    const scenarios = await queryUnderwritingScenarios('building', buildingId, {includeDeleted: true});
    await Promise.all(
      scenarios.map((scenario) =>
        updateDocument(UNDERWRITING_SCENARIOS_COLLECTION, scenario.id, {
          deleted: null,
          editedOn,
        }),
      ),
    );
  }
}

/**
 * Soft-deletes a comp from the base underwriting scenario when the comp is deleted via the info
 * window. Sets `deleted` on the matching comp in the array so the dashboard filters it out while
 * still allowing the operation to be undone.
 */
export async function reflectCompDeletion(
  buildingId: string, compId: string, knownPortfolioId?: string | null,
): Promise<void> {
  const portfolioId = await resolvePortfolioId(buildingId, knownPortfolioId);
  const editedOn = buildEditedOn();

  if (portfolioId) {
    const scenarios = await queryUnderwritingScenarios('portfolio', portfolioId);
    const baseScenario = scenarios.find((s) => isPortfolioScenario(s) && s.isBase);
    if (!baseScenario || !isPortfolioScenario(baseScenario)) {
      throw new Error(`No base portfolio scenario found for portfolio ${portfolioId}`);
    }

    const buildingSnapshot = baseScenario.buildings[buildingId];
    if (!buildingSnapshot) return;

    const updatedComps = buildingSnapshot.comps.map((c) =>
      c.id === compId ? {...c, deleted: editedOn.timestamp} : c,
    );
    await updateDocument(UNDERWRITING_SCENARIOS_COLLECTION, baseScenario.id, {
      [`buildings.${buildingId}.comps`]: updatedComps,
      editedOn,
    });
  } else {
    const scenarios = await queryUnderwritingScenarios('building', buildingId);
    const baseScenario = scenarios.find((s) => s.isBase);
    if (!baseScenario) {
      throw new Error(`No base building scenario found for building ${buildingId}`);
    }

    const updatedComps = baseScenario.comps.map((c) =>
      c.id === compId ? {...c, deleted: editedOn.timestamp} : c,
    );
    await updateDocument(UNDERWRITING_SCENARIOS_COLLECTION, baseScenario.id, {
      comps: updatedComps,
      editedOn,
    });
  }
}

/**
 * Clears the soft-delete on a comp in the base underwriting scenario, restoring it to the dashboard.
 * Called when the user undoes a comp deletion.
 */
export async function reflectCompUndoDeletion(
  buildingId: string, compId: string, knownPortfolioId?: string,
): Promise<void> {
  const portfolioId = await resolvePortfolioId(buildingId, knownPortfolioId);
  const editedOn = buildEditedOn();

  if (portfolioId) {
    const scenarios = await queryUnderwritingScenarios('portfolio', portfolioId);
    const baseScenario = scenarios.find((s) => isPortfolioScenario(s) && s.isBase);
    if (!baseScenario || !isPortfolioScenario(baseScenario)) {
      throw new Error(`No base portfolio scenario found for portfolio ${portfolioId}`);
    }

    const buildingSnapshot = baseScenario.buildings[buildingId];
    if (!buildingSnapshot) return;

    const updatedComps = buildingSnapshot.comps.map((c) =>
      c.id === compId ? {...c, deleted: null} : c,
    );
    await updateDocument(UNDERWRITING_SCENARIOS_COLLECTION, baseScenario.id, {
      [`buildings.${buildingId}.comps`]: updatedComps,
      editedOn,
    });
  } else {
    const scenarios = await queryUnderwritingScenarios('building', buildingId);
    const baseScenario = scenarios.find((s) => s.isBase);
    if (!baseScenario) {
      throw new Error(`No base building scenario found for building ${buildingId}`);
    }

    const updatedComps = baseScenario.comps.map((c) =>
      c.id === compId ? {...c, deleted: null} : c,
    );
    await updateDocument(UNDERWRITING_SCENARIOS_COLLECTION, baseScenario.id, {
      comps: updatedComps,
      editedOn,
    });
  }
}
